import { z } from 'zod';

import { apiRequest } from '@/lib/api-client';
import { cagrNullReasonField, consistentCagr } from '@/lib/cagr-null-reason';
import { netOfPendingTwin } from '@/lib/net-of-pending';

import {
  investorAggregateEnvelopeSchema,
  investorMetricWindowSchema,
  investorScopeSchema
} from './investor-reads';
import {
  assertAnalyticsResponseScope,
  normalizeAnalyticsAddress
} from './schemas';

/**
 * The investor's performance series (`/performance`: value, the flow
 * adjusted index, drawdown, benchmarks) and daily return series
 * (`/returns`), with their reads. Shared here because Portfolio and Risk &
 * liquidity both chart them.
 */

const finiteNumber = z.number().finite();
const nonNegativeNumber = finiteNumber.nonnegative();
const isoDate = z.union([z.iso.date(), z.iso.datetime({ offset: true })]);
const nullableIsoDate = isoDate.nullable();
const boundedLabel = z.string().trim().min(1).max(256);

/**
 * The calendar day of a timestamp, `YYYY-MM-DD`, whether the API stamped a
 * date or a date and time. The one place series, windows and flows are
 * reduced to days, so they always line up.
 */
export const dayOf = (timestamp: string) => timestamp.slice(0, 10);

const investorPerformanceQuerySchema = z.object({
  from: isoDate.optional(),
  to: isoDate.optional(),
  interval: z.enum(['1d', '1w']).optional(),
  benchmarks: z.array(boundedLabel).max(20).optional()
});

export type InvestorPerformanceQuery = z.infer<
  typeof investorPerformanceQuerySchema
>;

const performancePointSchema = z.object({
  timestamp: z.iso.date(),
  value: finiteNumber
});

const benchmarkSeriesSchema = z.object({
  symbol: boundedLabel,
  name: boundedLabel,
  series: z.array(performancePointSchema)
});

const drawdownPointSchema = z.object({
  timestamp: z.iso.date(),
  drawdownBps: nonNegativeNumber
});

/**
 * v1.60.0 — every metric here is nullable, and null means UNDEFINED, never
 * zero. A brand new investor's whole block comes back null where it used to
 * come back zeros, so parsing these as required numbers would reject the
 * response and blank the entire performance surface on first connect.
 *
 * Per-field null conditions, from the spec:
 *   cagr / totalReturn  — fewer than 2 points, or a zero base
 *   sharpeRatio         — fewer than 2 usable returns, or zero variance
 *   sortinoRatio        — fewer than 2 usable returns (0 is a real answer
 *                         here: it means no negative excess return)
 *   maxDrawdown         — fewer than 2 points, or never holds a positive value
 *   volatility          — no usable return
 *   calmarRatio         — maxDrawdown is 0, or cagr is null
 */
const totalReturnNetOfPending = netOfPendingTwin(
  'investor performance metrics',
  { value: 'totalReturnNetOfPending', reason: 'totalReturnNetOfPendingReason' }
);

const investorPerformanceMetricsSchema = z
  .object({
    cagr: finiteNumber.nullable(),
    // v1.156.0: why `cagr` is null (always under a year of history). Read
    // strictly; display is unchanged, as no investor surface prints a CAGR.
    cagrNullReason: cagrNullReasonField('investor performance metrics'),
    sharpeRatio: finiteNumber.nullable(),
    sortinoRatio: finiteNumber.nullable(),
    maxDrawdown: nonNegativeNumber.nullable(),
    maxDrawdownStart: nullableIsoDate,
    maxDrawdownEnd: nullableIsoDate,
    volatility: finiteNumber.nullable(),
    totalReturn: finiteNumber.nullable(),
    // API 1.159.0: a fraction like `totalReturn`, after fees still building
    // up too. Read, not displayed: no investor surface prints this figure.
    ...totalReturnNetOfPending.shape,
    calmarRatio: finiteNumber.nullable()
  })
  .transform(consistentCagr('investor performance metrics'))
  .transform(totalReturnNetOfPending.read);

/**
 * BE-000A, shipped in API 1.58.0: the flow-adjusted, time-weighted index the
 * scalar `metrics` are computed from, on the same grid as `portfolio`.
 *
 * A level is null where the sub-period could not be computed — no prior-day
 * base, a flow-dominated day, a negative residual. The backend is explicit
 * that null BREAKS the line on purpose and must never be drawn as a value,
 * so the chart leaves a gap rather than bridging it.
 *
 * `flowTiming` is carried through rather than assumed. The served convention
 * is end-of-period, r_t = (V_t − F_t) / V_{t−1} − 1; the start-of-period
 * alternative yields different numbers on the same data, which is exactly
 * why the frontend must not compute this itself.
 */
const portfolioIndexPointSchema = z.object({
  timestamp: isoDate,
  value: finiteNumber.nullable()
});

const portfolioIndexSchema = z.object({
  base: finiteNumber,
  flowTiming: z.string().max(64),
  flowTimingNote: z.string().max(512),
  points: z.array(portfolioIndexPointSchema),
  excludedPeriods: nonNegativeNumber
});

const investorPerformanceResponseSchema = investorScopeSchema.extend({
  from: nullableIsoDate,
  to: isoDate,
  interval: z.enum(['1d', '1w']),
  firstDepositAt: nullableIsoDate,
  portfolio: z.array(performancePointSchema),
  benchmarks: z.array(benchmarkSeriesSchema),
  warnings: z.array(z.string().max(512)),
  drawdown: z.array(drawdownPointSchema),
  metrics: investorPerformanceMetricsSchema,
  // Optional so a pre-1.58.0 environment still parses; the chart falls back
  // to plotting value alone when it is absent.
  portfolioIndex: portfolioIndexSchema.optional(),
  // Served since v1.31.0, parsed since v1.89.0: /performance always covers
  // the rowa-managed population, and totalPositions is the authoritative
  // ledger position count. Nullish for safety across environments.
  populationScope: z.string().max(64).optional(),
  totalPositions: nonNegativeNumber.int().nullish()
});

export type InvestorPerformance = z.infer<
  typeof investorPerformanceResponseSchema
>;

// ---------------------------------------------------------------------------
// Daily flow-adjusted return series (backend v1.49.0). returnBps carries
// FRACTIONAL precision by contract — the one deliberate exception to the
// integer bps convention: sub-bp daily returns would quantize to zero and
// every derived statistic (rolling volatility, histograms, historical VaR)
// would be computed from rounding noise. Guard-dropped days are absent,
// never zero — consumers index by date and never assume contiguity.
// ---------------------------------------------------------------------------

const investorReturnsResponseSchema = investorAggregateEnvelopeSchema
  .extend({
    returns: z
      .array(
        z.object({
          date: z.iso.date(),
          returnBps: finiteNumber
        })
      )
      .max(20_000),
    provenance: z.array(
      z.object({
        metricId: boundedLabel,
        formulaVersion: boundedLabel,
        windowDays: nonNegativeNumber.int().optional()
      })
    )
  })
  .superRefine((response, context) => {
    for (let index = 1; index < response.returns.length; index += 1) {
      if (response.returns[index]!.date <= response.returns[index - 1]!.date) {
        context.addIssue({
          code: 'custom',
          path: ['returns', index, 'date'],
          message: 'Return series is not strictly ascending by date'
        });
        break;
      }
    }
  });

export type InvestorReturnsResponse = z.infer<
  typeof investorReturnsResponseSchema
>;

const investorReturnsQuerySchema = z.object({
  window: investorMetricWindowSchema.optional()
});
type InvestorReturnsQuery = z.infer<typeof investorReturnsQuerySchema>;

interface InvestorPerformanceHint {
  /**
   * The `from` the last response for this same window was resolved to.
   *
   * A period that starts before the first deposit comes back empty and is
   * asked again from the deposit day, so every poll of that window used to
   * cost two requests. The caller reads this from the query's own cached
   * data (`usePerformanceChart`) and the fetcher starts there directly. It
   * is a hint about the same request, held in the query cache with the
   * response it came from: no state lives in this module.
   */
  resolvedFrom?: string | null;
}

export async function fetchInvestorPerformance(
  address: string,
  chainId: number,
  params: InvestorPerformanceQuery = {},
  signal?: AbortSignal,
  hint: InvestorPerformanceHint = {}
): Promise<InvestorPerformance> {
  const investor = normalizeAnalyticsAddress(address);
  const query = investorPerformanceQuerySchema.parse(params);

  const request = (from: string | undefined) =>
    apiRequest(
      `/api/v1/investor/${encodeURIComponent(investor)}/performance`,
      investorPerformanceResponseSchema,
      {
        auth: 'required',
        chainId,
        query: {
          from,
          to: query.to,
          interval: query.interval,
          // No selection means no parameter. An empty `benchmarks=` is a
          // different request to the backend than leaving it out.
          benchmarks:
            query.benchmarks != null && query.benchmarks.length > 0
              ? query.benchmarks.join(',')
              : undefined
        },
        signal
      }
    );

  /** A later start moves the window only when it falls inside it. */
  const startsBefore = (day: string) =>
    query.from != null &&
    day > dayOf(query.from) &&
    (query.to == null || day <= dayOf(query.to));

  const resolvedDay =
    hint.resolvedFrom != null ? dayOf(hint.resolvedFrom) : undefined;
  const startDay =
    resolvedDay != null && startsBefore(resolvedDay) ? resolvedDay : undefined;
  let response = await request(startDay ?? query.from);

  // A period that starts before the wallet's first deposit (YTD selected a
  // week after funding) comes back empty. The response names the first
  // deposit, so retry once from there: a young portfolio still charts the
  // history it actually has instead of a blank panel.
  if (response.portfolio.length === 0 && response.firstDepositAt != null) {
    const firstDepositDay = dayOf(response.firstDepositAt);
    if (firstDepositDay !== startDay && startsBefore(firstDepositDay)) {
      response = await request(firstDepositDay);
    }
  }

  assertAnalyticsResponseScope(investor, chainId, response);
  return response;
}

export async function fetchInvestorReturns(
  address: string,
  chainId: number,
  params: InvestorReturnsQuery = {},
  signal?: AbortSignal
): Promise<InvestorReturnsResponse> {
  const investor = normalizeAnalyticsAddress(address);
  const query = investorReturnsQuerySchema.parse(params);
  const response = await apiRequest(
    `/api/v1/investor/${encodeURIComponent(investor)}/returns`,
    investorReturnsResponseSchema,
    {
      auth: 'required',
      chainId,
      query,
      signal
    }
  );

  assertAnalyticsResponseScope(investor, chainId, response);
  return response;
}
